import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import * as z from "zod";
import { BASE_URL } from "../utils/utilsFunction";
import toast from "react-hot-toast";

const Contact = () => {
  // Define the Zod schema for form validation
  const contactSchema = z.object({
    name: z.string().min(1, "Name is required").max(100, "Name is too long"),
    email: z
      .string()
      .min(1, "Email is required")
      .email("Invalid email address")
      .max(100, "Email is too long"),
    phone: z
      .string()
      .min(10, "Phone number must be at least 10 digits")
      .max(10, "Phone number must be at most 10 digits")
      .regex(/^[0-9]+$/, "Phone number can only contain numbers"),

    enquiry: z
      .string()
      .min(1, "Enquiry is required")
      .max(500, "Enquiry is too long"),
  });

  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/v1/fieldexecutive/send-enquiry`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(values),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      toast.success("Form submitted successfully!");
      reset();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-grow flex-col flex items-center justify-center py-24">
        <div className="container mx-auto py-10 px-4">
          <div className="flex justify-center">
            <div className="card p-8 shadow-xl max-w-3xl w-full bg-white rounded-lg border border-gray-200">
              <header className="text-center mb-8">
                <h1 className="font-bold text-2xl text-[#2c3e50]">
                  Contact Form
                </h1>
              </header>
              <section>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="space-y-2">
                    <label
                      htmlFor="name"
                      className="font-medium text-lg text-gray-700"
                    >
                      Name
                    </label>
                    <input
                      id="name"
                      type="text"
                      placeholder="Your Name"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      {...register("name", { required: "Name is required" })}
                    />
                    {errors.name && (
                      <span className="text-sm text-red-500">
                        {errors.name.message}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="font-medium text-lg text-gray-700"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      placeholder="Your Email"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      {...register("email", { required: "Email is required" })}
                    />
                    {errors.email && (
                      <span className="text-sm text-red-500">
                        {errors.email.message}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="phone"
                      className="font-medium text-lg text-gray-700"
                    >
                      Phone Number
                    </label>
                    <input
                      id="phone"
                      type="text"
                      placeholder="Your Phone Number"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      {...register("phone", {
                        required: "Phone number is required",
                      })}
                    />
                    {errors.phone && (
                      <span className="text-sm text-red-500">
                        {errors.phone.message}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="enquiry"
                      className="font-medium text-lg text-gray-700"
                    >
                      Enquiry
                    </label>
                    <textarea
                      id="enquiry"
                      placeholder="Tell us about your enquiry..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none h-32"
                      {...register("enquiry", {
                        required: "Enquiry is required",
                      })}
                    />
                    {errors.enquiry && (
                      <span className="text-sm text-red-500">
                        {errors.enquiry.message}
                      </span>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-[#012647] text-white px-6 py-3 rounded-lg hover:bg-[#75000e] transition"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="animate-spin text-white mx-auto" />
                    ) : (
                      "Submit"
                    )}
                  </button>
                </form>
              </section>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
